import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, Loader2, AlertCircle } from 'lucide-react';
import { publicBookingApi, getErrorMessage } from '../../lib/http';
import type { ExpertService } from '../../types';

interface PublicBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  expertSlug: string;
  expertName: string;
  service: ExpertService;
  slotStart: string;
  onSuccess: () => void;
  initialData?: { name?: string; email?: string; phone?: string };
  timezone?: string;
}

type Step = 'details' | 'confirm';

const PublicBookingModal: React.FC<PublicBookingModalProps> = ({
  isOpen,
  onClose,
  expertSlug,
  expertName,
  service,
  slotStart,
  onSuccess,
  initialData,
  timezone,
}) => {
  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [notes, setNotes] = useState('');

  // UI state
  const [step, setStep] = useState<Step>('details');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill form fields when initialData becomes available
  useEffect(() => {
    if (initialData) {
      setName(prev => prev || initialData.name || '');
      setEmail(prev => prev || initialData.email || '');
      setPhone(prev => prev || initialData.phone || '');
    }
  }, [initialData]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '');
      setEmail(initialData?.email || '');
      setPhone(initialData?.phone || '');
      setNotes('');
      setStep('details');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDateTime = (isoString: string): { date: string; time: string } => {
    const date = new Date(isoString);
    const tz = timezone || 'UTC';
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: tz,
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: tz,
      }),
    };
  };

  const formatPrice = (cents: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  };

  const { date, time } = formatDateTime(slotStart);

  const handleConfirmBooking = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await publicBookingApi.create({
        service_id: service.id,
        provider_id: expertSlug,
        starts_at: slotStart,
        notes: notes.trim() || undefined,
        client: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
      });

      onSuccess();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('This time slot has already been booked. Please select another time.');
      } else if (err.response?.status === 422) {
        const apiError = err.response?.data?.error;
        setError(apiError?.message || apiError || 'Unable to complete booking. Please check your details.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateDetails = (): boolean => {
    if (!name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!email.trim()) {
      setError('Please enter your email');
      return false;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    setError(null);
    if (validateDetails()) {
      setStep('confirm');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'details' && 'Book Appointment'}
            {step === 'confirm' && 'Confirm Booking'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Booking Summary */}
        <div className="p-4 bg-purple-50 border-b border-purple-100">
          <p className="text-sm font-medium text-purple-900 mb-2">Booking with {expertName}</p>
          <div className="space-y-1 text-sm text-purple-700">
            <p className="font-medium">{service.name}</p>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{time} ({service.duration_min} min)</span>
            </div>
            <p className="font-semibold text-purple-900 mt-2">
              {formatPrice(service.price_cents, service.currency)}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Step 1: Details */}
          {step === 'details' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1234567890"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or information..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          )}

          {/* Step 2: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium text-gray-900">{name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium text-gray-900">{email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-medium text-gray-900">{phone}</span>
                </div>
                {notes && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="text-gray-500 block mb-1">Notes</span>
                    <span className="text-gray-900">{notes}</span>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-500 text-center">
                You will receive a confirmation email at {email}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={step === 'confirm' ? () => setStep('details') : onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {step === 'confirm' ? 'Back' : 'Cancel'}
          </button>

          {step === 'details' && (
            <button
              onClick={handleContinue}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 transition-colors"
            >
              Continue
            </button>
          )}

          {step === 'confirm' && (
            <button
              onClick={handleConfirmBooking}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Booking...' : 'Confirm Booking'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingModal;
